import { useTranslations } from "next-intl";

/**
 * Minimal translator shape used by `formatPurposeCodeLabel`. Structurally
 * compatible with the function returned by `useTranslations(...)` — we only
 * use the call signature and the `.has()` method, so we avoid pulling in
 * next-intl's narrow key types and keep the helper testable in isolation
 * (no React render, no DOM, no @testing-library/react).
 */
type PurposeCodeTranslator = {
  (key: string): string;
  has: (key: string) => boolean;
};

/**
 * Pure helper: resolves a backend `purposeCode` value to its translated
 * label, falling back to the raw value if the translation is missing.
 * Extracted so the test suite can exercise it without rendering a React
 * tree — see `formatPurposeCode.test.ts` and `.claude/docs/incident-log.md`.
 */
export function formatPurposeCodeLabel(
  value: string | undefined,
  t: PurposeCodeTranslator
): string | null {
  if (!value) return null;
  return t.has(value) ? t(value) : value;
}

export function usePurposeCodeLabel() {
  const tPaymentReason = useTranslations("PaymentReason");
  return (value: string | undefined): string | null =>
    formatPurposeCodeLabel(value, tPaymentReason as PurposeCodeTranslator);
}
